import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { attendanceAPI, employeesAPI } from '../services/api';
import MonthlyReport from '../components/MonthlyReport';
import {
  Clock,
  Users,
  Edit,
  Trash2,
  Search,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  UserCheck,
  BarChart3,
  X,
  FileSpreadsheet
} from 'lucide-react';
import { format } from 'date-fns';
import { formatLocalTime, formatNepalTime, localTimeToUTC, getTodayLocalDate } from '../utils/timeUtils';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

const Attendance = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('daily');
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [attendanceStats, setAttendanceStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedDate, setSelectedDate] = useState(getTodayLocalDate());
  const [showManualMarkModal, setShowManualMarkModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [isSubmittingManual, setIsSubmittingManual] = useState(false);
  const [absentEmployees, setAbsentEmployees] = useState([]);
  const [absentAttendance, setAbsentAttendance] = useState([]);
  const [isAutoCalculating, setIsAutoCalculating] = useState(false);

  const [manualMarkForm, setManualMarkForm] = useState({
    employeeId: '',
    date: getTodayLocalDate(),
    checkInTime: '09:00',
    checkOutTime: '',
    status: 'present',
    notes: '',
    breakTime: 0
  });

  const [editForm, setEditForm] = useState({
    checkInTime: '',
    checkOutTime: '',
    status: 'present',
    notes: '',
    breakTime: 0
  });

  const statusOptions = ['present', 'absent', 'late', 'half-day'];

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [attendanceRes, employeesRes, statsRes, absentRes] = await Promise.all([
        attendanceAPI.getTodayAttendance(selectedDate),
        employeesAPI.getAll(),
        attendanceAPI.getStats(selectedDate),
        attendanceAPI.getAbsentEmployees(selectedDate)
      ]);

      const allAttendance = attendanceRes.data;
      setTodayAttendance(allAttendance.filter(att => att.status !== 'absent'));
      setAbsentAttendance(allAttendance.filter(att => att.status === 'absent' && att._id));
      setEmployees(employeesRes.data);
      setAttendanceStats(statsRes.data);
      setAbsentEmployees(absentRes.data?.employees || []);
    } catch (error) {
      toast.error('Failed to load attendance data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate]);

  const filterAttendance = useCallback(() => {
    setFilteredAttendance([]);
    setTimeout(() => {
      let filtered;

      if (filterStatus === 'absent') {
        const noRecordAbsent = absentEmployees.filter(emp =>
          !absentAttendance.some(att => att.employeeId?._id === emp._id)
        );

        const syntheticAbsent = noRecordAbsent.map(emp => ({
          _id: null,
          employeeId: emp,
          status: 'absent',
          date: selectedDate,
        }));

        filtered = [...absentAttendance, ...syntheticAbsent];

        if (searchTerm) {
          filtered = filtered.filter(att =>
            att.employeeId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            att.employeeId?.employeeId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            att.employeeId?.department?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
      } else {
        filtered = todayAttendance;

        if (searchTerm) {
          filtered = filtered.filter(att =>
            att.employeeId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            att.employeeId?.employeeId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            att.employeeId?.department?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }

        if (filterStatus) {
          filtered = filtered.filter(att => att.status === filterStatus);
        }
      }

      setFilteredAttendance(filtered);
    }, 0);
  }, [filterStatus, absentEmployees, absentAttendance, searchTerm, todayAttendance, selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    filterAttendance();
  }, [filterAttendance]);

  const handleManualMark = async (e) => {
    e.preventDefault();
    if (isSubmittingManual) return;

    setIsSubmittingManual(true);
    try {
      const existingRecord = todayAttendance.find(att =>
        att.employeeId?._id === manualMarkForm.employeeId &&
        format(new Date(att.date), 'yyyy-MM-dd') === manualMarkForm.date
      );

      if (existingRecord) {
        toast.error('Attendance already exists for this employee on this date.');
        setShowManualMarkModal(false);
        resetManualMarkForm();
        setIsSubmittingManual(false);
        return;
      }

      const checkInLocal = new Date(`${manualMarkForm.date}T${manualMarkForm.checkInTime}`);
      const checkOutLocal = manualMarkForm.checkOutTime ? new Date(`${manualMarkForm.date}T${manualMarkForm.checkOutTime}`) : null;

      const attendanceData = {
        employeeId: manualMarkForm.employeeId,
        date: manualMarkForm.date,
        checkInTime: localTimeToUTC(checkInLocal).toISOString(),
        checkOutTime: checkOutLocal ? localTimeToUTC(checkOutLocal).toISOString() : null,
        status: manualMarkForm.status,
        notes: manualMarkForm.notes,
        breakTime: parseInt(manualMarkForm.breakTime) || 0,
        loginType: 'manual',
        isManualEntry: true
      };

      await attendanceAPI.markManualAttendance(attendanceData);
      toast.success('Attendance marked successfully');
      setShowManualMarkModal(false);
      resetManualMarkForm();
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to mark attendance');
    } finally {
      setIsSubmittingManual(false);
    }
  };

  const handleEditAttendance = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        date: selectedDate,
        checkInTime: localTimeToUTC(new Date(`${selectedDate}T${editForm.checkInTime}`)).toISOString(),
        checkOutTime: editForm.checkOutTime ?
          localTimeToUTC(new Date(`${selectedDate}T${editForm.checkOutTime}`)).toISOString() : null,
        status: editForm.status,
        notes: editForm.notes,
        breakTime: parseInt(editForm.breakTime) || 0
      };

      await attendanceAPI.updateAttendance(selectedAttendance._id, updateData);
      toast.success('Attendance updated successfully');
      setShowEditModal(false);
      setSelectedAttendance(null);
      resetEditForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to update attendance');
    }
  };

  const handleDeleteAttendance = async (attendanceId, employeeName) => {
    if (window.confirm(`Are you sure you want to delete attendance record for ${employeeName}?`)) {
      try {
        await attendanceAPI.deleteAttendance(attendanceId);
        toast.success('Attendance record deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete attendance record');
      }
    }
  };

  const handleAutoCalculateWorkingHours = async () => {
    if (isAutoCalculating) return;
    if (!window.confirm(`Auto-calculate working hours for all employees who haven't checked out on ${selectedDate}? This will set their check-out time to their shift end time.`)) {
      return;
    }

    setIsAutoCalculating(true);
    try {
      const response = await attendanceAPI.autoCalculateWorkingHours(selectedDate);
      const data = response.data || response;
      const message = data.message || `Auto-calculated working hours for ${data.updatedRecords?.length || 0} employees`;
      toast.success(message);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to auto-calculate working hours');
    } finally {
      setIsAutoCalculating(false);
    }
  };

  const openEditModal = (attendance) => {
    setSelectedAttendance(attendance);
    setEditForm({
      checkInTime: formatLocalTime(attendance.checkInTime, 'HH:mm'),
      checkOutTime: attendance.checkOutTime ? formatLocalTime(attendance.checkOutTime, 'HH:mm') : '',
      status: attendance.status,
      notes: attendance.notes || '',
      breakTime: attendance.breakTime || 0
    });
    setShowEditModal(true);
  };

  const resetManualMarkForm = () => {
    setManualMarkForm({
      employeeId: '',
      date: getTodayLocalDate(),
      checkInTime: '09:00',
      checkOutTime: '',
      status: 'present',
      notes: '',
      breakTime: 0
    });
  };

  const resetEditForm = () => {
    setEditForm({
      checkInTime: '',
      checkOutTime: '',
      status: 'present',
      notes: '',
      breakTime: 0
    });
  };

  const exportAttendanceData = async () => {
    try {
      const response = await attendanceAPI.getAttendanceReport(selectedDate, selectedDate);
      const csvContent = convertToCSV(response.data);
      downloadCSV(csvContent, `attendance-${selectedDate}.csv`);
      toast.success('Attendance data exported successfully');
    } catch (error) {
      toast.error('Failed to export attendance data');
    }
  };

  const convertToCSV = (data) => {
    const headers = ['Employee ID', 'Name', 'Department', 'Date', 'Check In', 'Check Out', 'Status', 'Working Hours', 'Notes'];
    const rows = data.map(record => [
      record.employeeId?.employeeId || '',
      record.employeeId?.name || '',
      record.employeeId?.department || '',
      format(new Date(record.date), 'yyyy-MM-dd'),
      formatLocalTime(record.checkInTime, 'HH:mm'),
      record.checkOutTime ? formatLocalTime(record.checkOutTime, 'HH:mm') : '',
      record.status,
      record.workingHours ? `${record.workingHours.toFixed(2)} hours` : '',
      record.notes || ''
    ]);

    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const tabs = [
    { id: 'daily', name: 'Daily View', icon: Clock },
    { id: 'monthly', name: 'Monthly Report', icon: BarChart3 }
  ];

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-16">
        <h1 className="text-lg font-semibold text-slate-800">Access Denied</h1>
        <p className="text-slate-500 mt-2">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            Attendance Manager
          </h1>
          <p className="text-slate-500 mt-1">Monitor daily logs and generate reports</p>
        </div>

        {activeTab === 'daily' && (
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={exportAttendanceData}
              className="inline-flex items-center px-4 py-2 border border-slate-200 bg-white text-slate-600 font-semibold rounded-md hover:bg-slate-50 transition-colors shadow-sm"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
            <button
              onClick={handleAutoCalculateWorkingHours}
              disabled={isAutoCalculating}
              className={`inline-flex items-center px-4 py-2 border border-gray-200 bg-gray-100 text-gray-900 font-semibold rounded-md hover:bg-gray-100 transition-colors shadow-sm ${isAutoCalculating ? 'opacity-75 cursor-wait' : ''}`}
            >
              <Clock className="w-4 h-4 mr-2" />
              {isAutoCalculating ? 'Processing...' : 'Auto-Sync Hours'}
            </button>
            <button
              onClick={() => setShowManualMarkModal(true)}
              className="inline-flex items-center px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md shadow-none transition-all  "
            >
              <UserCheck className="w-4 h-4 mr-2" />
              Mark Attendance
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white p-1.5 rounded-lg shadow-sm border border-slate-200 inline-flex">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-all ${activeTab === tab.id
              ? 'bg-gray-100 text-gray-900 shadow-sm ring-1 ring-gray-200'
              : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
          >
            <tab.icon size={16} />
            {tab.name}
          </button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'daily' ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-6"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: 'Total Staff', value: attendanceStats.totalEmployees || 0, icon: Users, color: 'blue' },
                { label: 'Present', value: attendanceStats.present || 0, icon: CheckCircle, color: 'green' },
                { label: 'Absent', value: attendanceStats.absent || 0, icon: XCircle, color: 'red' },
                { label: 'Late Arrival', value: attendanceStats.late || 0, icon: AlertCircle, color: 'yellow' }
              ].map((stat, i) => (
                <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-500 uppercase tracking-wide">{stat.label}</p>
                      <p className={`text-xl font-semibold mt-1 text-gray-900`}>{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-md bg-gray-100 text-gray-600`}>
                      <stat.icon size={24} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Filters */}
            <div className="bg-white p-2 rounded-lg shadow-sm border border-slate-200 flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search staff..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 bg-transparent border-0 focus:ring-0 text-sm placeholder:text-slate-400"
                />
              </div>
              <div className="h-8 w-px bg-slate-200 hidden sm:block self-center"></div>
              <div className="sm:w-48 px-2">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full py-2.5 bg-transparent border-0 focus:ring-0 text-sm text-slate-600 font-medium cursor-pointer"
                >
                  <option value="">All Status</option>
                  {statusOptions.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div className="h-8 w-px bg-slate-200 hidden sm:block self-center"></div>
              <div className="sm:w-48 px-2">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full py-2.5 bg-transparent border-0 focus:ring-0 text-sm text-slate-600 font-medium cursor-pointer"
                />
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-200 mb-4"></div>
                  <p className="text-slate-500 font-medium">Loading data...</p>
                </div>
              ) : filteredAttendance.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                    <FileSpreadsheet className="w-8 h-8 text-slate-300" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900">No records found</h3>
                  <p className="text-slate-500 mt-1 max-w-sm">No attendance records for date {selectedDate}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50/50 border-b border-slate-100 text-left">
                        <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Employee</th>
                        <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Check In</th>
                        <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Check Out</th>
                        <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Status</th>
                        <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Hours</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredAttendance.map((record) => (
                        <tr
                          key={record._id || `absent-${record.employeeId?._id || record.employeeId?.employeeId || 'unknown'}`}
                          className="hover:bg-slate-50/50 transition-colors group"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 font-bold">
                                {record.employeeId?.name?.[0] || '?'}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{record.employeeId?.name}</div>
                                <div className="text-xs text-slate-500 font-mono">{record.employeeId?.employeeId}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {record.status === 'absent' || !record.checkInTime ? (
                              <span className="text-slate-400 text-sm">—</span>
                            ) : (
                              <div className="font-medium text-slate-900">{formatNepalTime(record.checkInTime, 'HH:mm')}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {!record.checkOutTime ? (
                              <span className="text-slate-400 text-sm text-italic">Active</span>
                            ) : (
                              <div className="font-medium text-slate-900">{formatNepalTime(record.checkOutTime, 'HH:mm')}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide border ${record.status === 'present' ? 'bg-gray-100 text-gray-900 border-gray-200' :
                              record.status === 'late' ? 'bg-gray-100 text-gray-900 border-gray-200' :
                                record.status === 'absent' ? 'bg-gray-100 text-gray-700 border-gray-200' :
                                  'bg-gray-100 text-gray-900 border-gray-200'
                              }`}>
                              {record.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-mono font-medium text-slate-600">
                            {record.workingHours ? record.workingHours.toFixed(2) : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {record._id && (
                              <div className="flex items-center justify-end gap-2">
                                <button onClick={() => openEditModal(record)} className="p-2 text-slate-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                                  <Edit size={16} />
                                </button>
                                <button onClick={() => handleDeleteAttendance(record._id, record.employeeId?.name)} className="p-2 text-slate-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="monthly"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <MonthlyReport />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Manual Mark Modal */}
      <AnimatePresence>
        {showManualMarkModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-2 backdrop-blur-md sm:p-4">
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 18 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.96, opacity: 0, y: 18 }}
              className="relative flex max-h-[calc(100vh-1rem)] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-[0_30px_80px_rgba(15,23,42,0.28)] ring-1 ring-slate-200 sm:max-h-[calc(100vh-2rem)] sm:rounded-[28px]"
            >
              <div className="absolute inset-x-0 top-0 h-1.5 bg-gray-900" />

              <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 bg-gradient-to-b from-slate-50/90 to-white px-5 pb-4 pt-5 sm:px-8 sm:pb-6 sm:pt-8">
                <div className="min-w-0 space-y-1">
                  <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-gray-900 sm:text-[11px] sm:tracking-[0.2em]">
                    Manual Entry
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 sm:text-2xl">Mark Attendance</h3>
                  <p className="text-xs text-slate-500 sm:text-sm">Manually add an attendance record</p>
                </div>
                <button
                  onClick={() => setShowManualMarkModal(false)}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-all hover:border-slate-300 hover:bg-slate-100 hover:text-slate-700 sm:h-11 sm:w-11"
                  aria-label="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleManualMark} className="flex min-h-0 flex-1 flex-col">
                <div className="grid flex-1 gap-3 overflow-y-auto px-5 py-4 sm:gap-5 sm:px-8 sm:py-6 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 sm:mb-2 sm:text-[11px] sm:tracking-[0.2em]">Employee</label>
                    <select
                      value={manualMarkForm.employeeId}
                      onChange={e => setManualMarkForm({ ...manualMarkForm, employeeId: e.target.value })}
                      required
                      className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition-all focus:border-gray-200 focus:bg-white focus:ring-4 focus:ring-gray-200 sm:rounded-lg sm:py-3.5"
                    >
                      <option value="">Select Employee</option>
                      {employees.map(e => <option key={e._id} value={e._id}>{e.name} ({e.employeeId})</option>)}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 sm:mb-2 sm:text-[11px] sm:tracking-[0.2em]">Date</label>
                    <input
                      type="date"
                      value={manualMarkForm.date}
                      onChange={e => setManualMarkForm({ ...manualMarkForm, date: e.target.value })}
                      required
                      className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition-all focus:border-gray-200 focus:bg-white focus:ring-4 focus:ring-gray-200 sm:rounded-lg sm:py-3.5"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 sm:mb-2 sm:text-[11px] sm:tracking-[0.2em]">Check In</label>
                    <input
                      type="time"
                      value={manualMarkForm.checkInTime}
                      onChange={e => setManualMarkForm({ ...manualMarkForm, checkInTime: e.target.value })}
                      required
                      className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition-all focus:border-gray-200 focus:bg-white focus:ring-4 focus:ring-gray-200 sm:rounded-lg sm:py-3.5"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 sm:mb-2 sm:text-[11px] sm:tracking-[0.2em]">Check Out</label>
                    <input
                      type="time"
                      value={manualMarkForm.checkOutTime}
                      onChange={e => setManualMarkForm({ ...manualMarkForm, checkOutTime: e.target.value })}
                      className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition-all focus:border-gray-200 focus:bg-white focus:ring-4 focus:ring-gray-200 sm:rounded-lg sm:py-3.5"
                    />
                  </div>

                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 sm:mb-2 sm:text-[11px] sm:tracking-[0.2em]">Status</label>
                    <select
                      value={manualMarkForm.status}
                      onChange={e => setManualMarkForm({ ...manualMarkForm, status: e.target.value })}
                      className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition-all focus:border-gray-200 focus:bg-white focus:ring-4 focus:ring-gray-200 sm:rounded-lg sm:py-3.5"
                    >
                      {statusOptions.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 sm:mb-2 sm:text-[11px] sm:tracking-[0.2em]">Break Time (min)</label>
                    <input
                      type="number"
                      value={manualMarkForm.breakTime}
                      onChange={e => setManualMarkForm({ ...manualMarkForm, breakTime: e.target.value })}
                      min="0"
                      className="w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 outline-none transition-all focus:border-gray-200 focus:bg-white focus:ring-4 focus:ring-gray-200 sm:rounded-lg sm:py-3.5"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-slate-100 bg-white px-5 py-4 sm:flex-row sm:justify-end sm:px-8">
                  <button
                    type="button"
                    onClick={() => setShowManualMarkModal(false)}
                    className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800 sm:rounded-lg sm:py-3"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center rounded-md bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white shadow-none transition-all hover:bg-gray-800 hover:shadow-xl hover:shadow-none/70 sm:rounded-lg sm:py-3"
                  >
                    Submit Record
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Modal Logic (reused similar structure) can be added here if not already present in reused code blocks logic */}
      <AnimatePresence>
        {showEditModal && selectedAttendance && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              className="bg-white rounded-lg w-full max-w-lg overflow-hidden shadow-2xl"
            >
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Edit Attendance Record</h3>
                <button onClick={() => setShowEditModal(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
              </div>

              <form onSubmit={handleEditAttendance} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Check In</label>
                    <input type="time" value={editForm.checkInTime} onChange={e => setEditForm({ ...editForm, checkInTime: e.target.value })} required className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Check Out</label>
                    <input type="time" value={editForm.checkOutTime} onChange={e => setEditForm({ ...editForm, checkOutTime: e.target.value })} className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Status</label>
                  <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-md text-sm">
                    {statusOptions.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Break Time (minutes)</label>
                  <input
                    type="number"
                    value={editForm.breakTime}
                    onChange={e => setEditForm({ ...editForm, breakTime: e.target.value })}
                    min="0"
                    className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-md focus:ring-2 focus:ring-gray-200 focus:border-transparent text-sm"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Notes</label>
                  <textarea value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} rows="2" className="w-full px-3 py-2.5 border border-slate-200 rounded-md text-sm" />
                </div>

                <div className="pt-4 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 border border-slate-200 rounded-md text-slate-600 font-semibold hover:bg-slate-50">Cancel</button>
                  <button type="submit" className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-md shadow-none">Save Changes</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Attendance;
